/**
 * @file apps/web/src/lib/api.ts
 * @description Typed API client for the Veridox Fastify backend.
 * All methods accept optional extra headers for dev bypass auth.
 */

import type { ApiResponse } from '@veridox/types';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3001';

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Fastify's default error shape (from reply.badRequest()/.notFound()/uncaught
// exceptions) is {statusCode, error, message} — "error" is a string, not the
// app's {code, message, details} object. Routes that don't build the app's
// own envelope fall through to this shape; normalize both here so every
// caller's `e.message` is always the real backend message, never blank.
interface FastifyNativeError {
  statusCode: number;
  error:      string;
  message:    string;
  code?:      string;
}

function toApiError(body: unknown, httpStatus: number): ApiError {
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    if (b['success'] === false && b['error'] && typeof b['error'] === 'object') {
      const e = b['error'] as { code?: string; message?: string; details?: unknown };
      return new ApiError(e.code ?? 'ERROR', e.message ?? 'An unexpected error occurred', e.details);
    }
    if (typeof b['message'] === 'string') {
      return new ApiError(String(b['statusCode'] ?? b['code'] ?? httpStatus), b['message'] as string, undefined);
    }
  }
  return new ApiError('UNKNOWN_ERROR', 'An unexpected error occurred', body);
}

async function request<T>(
  path: string,
  options?: RequestInit & { extraHeaders?: Record<string, string> },
): Promise<T> {
  const { extraHeaders, ...fetchOptions } = options ?? {};

  const res = await fetch(`${API_BASE}/api/v1${path}`, {
    ...fetchOptions,
    headers: {
      // Only send Content-Type: application/json when there's actually a
      // body — Fastify's JSON body parser rejects a request that declares
      // this content type but sends no body (e.g. every DELETE call here,
      // and bodyless POSTs like the discovery seed endpoint) with
      // "Body cannot be empty when content-type is set to 'application/json'".
      ...(fetchOptions.body ? { 'Content-Type': 'application/json' } : {}),
      ...extraHeaders,
      ...fetchOptions.headers,
    },
    credentials: 'include',
  });

  if (res.status === 204) return undefined as unknown as T;

  const body = await res.json() as ApiResponse<T> | FastifyNativeError;
  if (!('success' in body) || !body.success) {
    throw toApiError(body, res.status);
  }
  return body.data;
}

// Multipart (no Content-Type header — browser sets boundary)
async function requestFormData<T>(
  path: string,
  formData: FormData,
  extraHeaders?: Record<string, string>,
): Promise<T> {
  const res = await fetch(`${API_BASE}/api/v1${path}`, {
    method: 'POST',
    headers: extraHeaders,
    body: formData,
    credentials: 'include',
  });

  const body = await res.json() as ApiResponse<T> | FastifyNativeError;
  if (!('success' in body) || !body.success) {
    throw toApiError(body, res.status);
  }
  return body.data;
}

// Binary download (e.g. .docx export) — the endpoint returns a raw file, not
// the {success, data} JSON envelope, so a plain <a href> can't carry auth
// headers and this fetches it as a Blob instead.
async function requestBlob(path: string, extraHeaders?: Record<string, string>): Promise<Blob> {
  const res = await fetch(`${API_BASE}/api/v1${path}`, {
    headers: extraHeaders,
    credentials: 'include',
  });
  if (!res.ok) {
    let message = `Download failed (${res.status})`;
    try {
      const body: unknown = await res.json();
      const err = toApiError(body, res.status);
      message = err.message;
    } catch { /* response wasn't JSON — keep the generic message */ }
    throw new ApiError('DOWNLOAD_FAILED', message);
  }
  return res.blob();
}

type H = Record<string, string>;

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------
export const authApi = {
  me: (h: H) =>
    request<{
      id: string; email: string; fullName: string | null; avatarUrl: string | null;
      tenantId: string; tenantRole: string;
      memberships: { tenant_id: string; role: string; tenant_name: string }[];
      projectId: string; role: string; userType: 'si' | 'client';
    }>('/auth/me', { extraHeaders: h }),
  updateMe:   (body: unknown, h: H) =>
    request<Record<string, unknown>>('/auth/me', { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
  login:      (email: string, password: string) =>
    request<{
      token: string;
      user: { userId: string; tenantId: string; email: string; fullName: string; projectId: string; role: string; tenantRole: string; userType: 'si' | 'client' };
    }>(
      '/auth/login',
      { method: 'POST', body: JSON.stringify({ email, password }) },
    ),
  devToken:   (body: { userId: string; tenantId: string }) =>
    request<{ token: string }>('/auth/dev-token', { method: 'POST', body: JSON.stringify(body) }),
  devLogin:   (email: string, password: string) =>
    request<{ userId: string; tenantId: string; email: string; fullName: string; projectId: string; role: string; tenantRole: string }>(
      '/auth/dev-login',
      { method: 'POST', body: JSON.stringify({ email, password }) },
    ),
};

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------
export const healthApi = {
  check: () => request<{ status: string; version: string }>('/health'),
};

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------
export const projectsApi = {
  list:    (tenantId: string, h: H) =>
    request<unknown[]>(`/projects?tenantId=${encodeURIComponent(tenantId)}`, { extraHeaders: h }),
  get:     (id: string, h: H) =>
    request<unknown>(`/projects/${id}`, { extraHeaders: h }),
  create:  (body: unknown, h: H) =>
    request<unknown>('/projects', { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  update:  (id: string, body: unknown, h: H) =>
    request<unknown>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
  delete:  (id: string, h: H) =>
    request<void>(`/projects/${id}`, { method: 'DELETE', extraHeaders: h }),
  members: (id: string, h: H) =>
    request<unknown[]>(`/projects/${id}/members`, { extraHeaders: h }),
  overview: (id: string, h: H) =>
    request<{
      phases: { phase: string; status: string; progress: number; stat: string }[];
      nextAction: { message: string; ctaLabel: string; ctaHref: string };
      quickStats: { testScripts: number; openDefects: number; pendingApprovals: number; sessions: number };
      uatGate:    { unlocked: boolean; requiresFddApproval: boolean; fddApproved: boolean };
      launchGate: { unlocked: boolean; requiresUatSignoff: boolean; uatSignoffApproved: boolean };
    }>(`/projects/${id}/overview`, { extraHeaders: h }),
};

// ---------------------------------------------------------------------------
// Discovery (skill tree)
// ---------------------------------------------------------------------------
export const discoveryApi = {
  get:        (projectId: string, h: H) =>
    request<Record<string, Record<string, unknown[]>>>(`/projects/${projectId}/discovery`, { extraHeaders: h }),
  getSummary: (projectId: string, h: H) =>
    request<{ module: string; total: number; reviewed: number; byDisposition: Record<string, number> }[]>(
      `/projects/${projectId}/discovery/summary`, { extraHeaders: h },
    ),
  updateNode: (projectId: string, nodeId: string, body: { disposition?: string; notes?: string }, h: H) =>
    request<unknown>(`/projects/${projectId}/discovery/${nodeId}`, {
      method: 'PATCH', body: JSON.stringify(body), extraHeaders: h,
    }),
  seed:       (projectId: string, h: H) =>
    request<{ seeded: number }>(`/projects/${projectId}/discovery/seed`, { method: 'POST', extraHeaders: h }),
  // Per-node FDD (docs/NEXT-PHASE-SCOPING.md §2) — DiscoveryPanel.tsx.
  getNodeFdd: (projectId: string, nodeId: string, h: H) =>
    request<{ status: 'none' | 'generated' | 'edited'; documentId: string | null; title: string | null }>(
      `/projects/${projectId}/discovery/${nodeId}/fdd`, { extraHeaders: h },
    ),
  generateNodeFdd: (projectId: string, nodeId: string, h: H) =>
    request<unknown>(`/projects/${projectId}/discovery/${nodeId}/generate-fdd`, { method: 'POST', extraHeaders: h }),
};

export const designItemsApi = {
  list:   (projectId: string, h: H) =>
    request<Record<string, unknown[]>>(`/projects/${projectId}/design-items`, { extraHeaders: h }),
  create: (projectId: string, body: unknown, h: H) =>
    request<unknown>(`/projects/${projectId}/design-items`, { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  update: (projectId: string, itemId: string, body: unknown, h: H) =>
    request<unknown>(`/projects/${projectId}/design-items/${itemId}`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
  delete: (projectId: string, itemId: string, h: H) =>
    request<void>(`/projects/${projectId}/design-items/${itemId}`, { method: 'DELETE', extraHeaders: h }),
};

// ---------------------------------------------------------------------------
// Tenants
// ---------------------------------------------------------------------------
export const tenantsApi = {
  get:    (id: string, h: H) =>
    request<unknown>(`/tenants/${id}`, { extraHeaders: h }),
  update: (id: string, body: unknown, h: H) =>
    request<unknown>(`/tenants/${id}`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
};

// ---------------------------------------------------------------------------
// Project invites and member management (project admins / workspace admins)
// ---------------------------------------------------------------------------
export type InviteRole = 'lead' | 'consultant' | 'client_admin' | 'client_user';

export interface ProjectInvite {
  id:              string;
  project_id:      string;
  email:           string;
  role:            string;
  status:          'pending' | 'claimed' | 'revoked' | 'expired';
  invited_by_name: string | null;
  expires_at:      string;
  last_sent_at:    string;
  created_at:      string;
}

export type InviteOutcome =
  | { outcome: 'invited' | 'resent'; invite: ProjectInvite; emailSent: boolean }
  | { outcome: 'linked'; invite: ProjectInvite };

export const projectInvitesApi = {
  list: (projectId: string, h: H) =>
    request<ProjectInvite[]>(`/projects/${encodeURIComponent(projectId)}/invites`, { extraHeaders: h }),
  invite: (projectId: string, body: { email: string; role: InviteRole }, h: H) =>
    request<InviteOutcome>(`/projects/${encodeURIComponent(projectId)}/invites`, { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  resend: (projectId: string, inviteId: string, h: H) =>
    request<{ invite: ProjectInvite; emailSent: boolean }>(
      `/projects/${encodeURIComponent(projectId)}/invites/${encodeURIComponent(inviteId)}/resend`, { method: 'POST', extraHeaders: h }),
  revoke: (projectId: string, inviteId: string, h: H) =>
    request<void>(`/projects/${encodeURIComponent(projectId)}/invites/${encodeURIComponent(inviteId)}`, { method: 'DELETE', extraHeaders: h }),
  changeRole: (projectId: string, userId: string, role: InviteRole, h: H) =>
    request<unknown>(`/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`, { method: 'PATCH', body: JSON.stringify({ role }), extraHeaders: h }),
  removeMember: (projectId: string, userId: string, h: H) =>
    request<void>(`/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`, { method: 'DELETE', extraHeaders: h }),
};

// ---------------------------------------------------------------------------
// Workspaces (tenant-level)
// ---------------------------------------------------------------------------
export const workspacesApi = {
  members: (tenantId: string, h: H) =>
    request<unknown[]>(`/tenants/${encodeURIComponent(tenantId)}/members`, { extraHeaders: h }),
  invite: (tenantId: string, body: { email: string; role: string }, h: H) =>
    request<unknown>(`/tenants/${encodeURIComponent(tenantId)}/members`, { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  removeMember: (tenantId: string, userId: string, h: H) =>
    request<void>(`/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}`, { method: 'DELETE', extraHeaders: h }),
  getSettings:    (tenantId: string, h: H) =>
    request<unknown>(`/tenants/${encodeURIComponent(tenantId)}/settings`, { extraHeaders: h }),
  updateSettings: (tenantId: string, body: unknown, h: H) =>
    request<unknown>(`/tenants/${encodeURIComponent(tenantId)}/settings`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
};

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------
export const clientsApi = {
  list:   (tenantId: string, h: H) =>
    request<unknown[]>(`/clients?tenantId=${encodeURIComponent(tenantId)}`, { extraHeaders: h }),
  get:    (id: string, h: H) => request<unknown>(`/clients/${id}`, { extraHeaders: h }),
  create: (body: unknown, h: H) =>
    request<unknown>('/clients', { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  update: (id: string, body: unknown, h: H) =>
    request<unknown>(`/clients/${id}`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
  delete: (id: string, h: H) =>
    request<void>(`/clients/${id}`, { method: 'DELETE', extraHeaders: h }),
};

// ---------------------------------------------------------------------------
// Test Scripts
// ---------------------------------------------------------------------------
export const testScriptsApi = {
  list:     (projectId: string, h: H, filters?: { subcycleId?: string; tagId?: string; untagged?: boolean; sessionId?: string }) => {
    const qs = new URLSearchParams({ projectId });
    if (filters?.subcycleId) qs.set('subcycleId', filters.subcycleId);
    if (filters?.tagId)      qs.set('tagId', filters.tagId);
    if (filters?.untagged)   qs.set('untagged', 'true');
    if (filters?.sessionId)  qs.set('sessionId', filters.sessionId);
    return request<unknown[]>(`/test-scripts?${qs}`, { extraHeaders: h });
  },
  get:      (id: string, h: H) =>
    request<unknown>(`/test-scripts/${id}`, { extraHeaders: h }),
  create:   (body: unknown, h: H) =>
    request<unknown>('/test-scripts', { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  update:   (id: string, body: unknown, h: H) =>
    request<unknown>(`/test-scripts/${id}`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
  delete:   (id: string, h: H) =>
    request<void>(`/test-scripts/${id}`, { method: 'DELETE', extraHeaders: h }),
  restore:  (id: string, h: H) =>
    request<unknown>(`/test-scripts/${id}/restore`, { method: 'POST', extraHeaders: h }),
  versions: (id: string, h: H) =>
    request<unknown[]>(`/test-scripts/${id}/versions`, { extraHeaders: h }),
  version:  (id: string, v: number, h: H) =>
    request<unknown>(`/test-scripts/${id}/versions/${v}`, { extraHeaders: h }),
  addStep:    (scriptId: string, body: unknown, h: H) =>
    request<unknown>(`/test-scripts/${scriptId}/steps`, { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  updateStep: (scriptId: string, stepId: string, body: unknown, h: H) =>
    request<unknown>(`/test-scripts/${scriptId}/steps/${stepId}`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
  deleteStep: (scriptId: string, stepId: string, h: H) =>
    request<void>(`/test-scripts/${scriptId}/steps/${stepId}`, { method: 'DELETE', extraHeaders: h }),
  // Module tag assignment (many-to-many)
  listTags:   (scriptId: string, h: H) =>
    request<unknown[]>(`/test-scripts/${scriptId}/tags`, { extraHeaders: h }),
  addTag:     (scriptId: string, tagId: string, h: H) =>
    request<unknown>(`/test-scripts/${scriptId}/tags`, { method: 'POST', body: JSON.stringify({ tagId }), extraHeaders: h }),
  removeTag:  (scriptId: string, tagId: string, h: H) =>
    request<void>(`/test-scripts/${scriptId}/tags/${tagId}`, { method: 'DELETE', extraHeaders: h }),
};

// ---------------------------------------------------------------------------
// UAT Cycles / Subcycles
// ---------------------------------------------------------------------------
export const uatCyclesApi = {
  list:           (projectId: string, h: H) =>
    request<unknown[]>(`/uat-cycles?projectId=${encodeURIComponent(projectId)}`, { extraHeaders: h }),
  get:            (id: string, h: H) =>
    request<unknown>(`/uat-cycles/${id}`, { extraHeaders: h }),
  create:         (body: unknown, h: H) =>
    request<unknown>('/uat-cycles', { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  update:         (id: string, body: unknown, h: H) =>
    request<unknown>(`/uat-cycles/${id}`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
  delete:         (id: string, h: H) =>
    request<void>(`/uat-cycles/${id}`, { method: 'DELETE', extraHeaders: h }),
  listSubcycles:  (cycleId: string, h: H) =>
    request<unknown[]>(`/uat-cycles/${cycleId}/subcycles`, { extraHeaders: h }),
  getSubcycle:    (cycleId: string, id: string, h: H) =>
    request<unknown>(`/uat-cycles/${cycleId}/subcycles/${id}`, { extraHeaders: h }),
  createSubcycle: (cycleId: string, body: unknown, h: H) =>
    request<unknown>(`/uat-cycles/${cycleId}/subcycles`, { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  updateSubcycle: (cycleId: string, id: string, body: unknown, h: H) =>
    request<unknown>(`/uat-cycles/${cycleId}/subcycles/${id}`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
  deleteSubcycle: (cycleId: string, id: string, h: H) =>
    request<void>(`/uat-cycles/${cycleId}/subcycles/${id}`, { method: 'DELETE', extraHeaders: h }),
};

// ---------------------------------------------------------------------------
// UAT Gaps
// ---------------------------------------------------------------------------
export const uatGapsApi = {
  list: (subcycleId: string, h: H) =>
    request<unknown[]>(`/uat-gaps?subcycleId=${encodeURIComponent(subcycleId)}`, { extraHeaders: h }),
};

// ---------------------------------------------------------------------------
// Module Tags
// ---------------------------------------------------------------------------
export const moduleTagsApi = {
  list:   (projectId: string, h: H) =>
    request<unknown[]>(`/module-tags?projectId=${encodeURIComponent(projectId)}`, { extraHeaders: h }),
  create: (body: unknown, h: H) =>
    request<unknown>('/module-tags', { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  update: (id: string, body: unknown, h: H) =>
    request<unknown>(`/module-tags/${id}`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
  delete: (id: string, h: H) =>
    request<void>(`/module-tags/${id}`, { method: 'DELETE', extraHeaders: h }),
};

// ---------------------------------------------------------------------------
// UAT
// ---------------------------------------------------------------------------
export const uatApi = {
  listSessions:   (projectId: string, h: H) =>
    request<unknown[]>(`/uat/sessions?projectId=${encodeURIComponent(projectId)}`, { extraHeaders: h }),
  getSession:     (id: string, h: H) =>
    request<unknown>(`/uat/sessions/${id}`, { extraHeaders: h }),
  createSession:  (body: unknown, h: H) =>
    request<unknown>('/uat/sessions', { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  updateSession:  (id: string, body: unknown, h: H) =>
    request<unknown>(`/uat/sessions/${id}`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
  getResults:     (sessionId: string, h: H) =>
    request<unknown[]>(`/uat/sessions/${sessionId}/results`, { extraHeaders: h }),
  recordResult:   (sessionId: string, body: unknown, h: H) =>
    request<unknown>(`/uat/sessions/${sessionId}/results`, { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  // Defects
  listDefects:    (projectId: string, h: H, params?: { sessionId?: string; status?: string; subcycleId?: string }) => {
    const qs = new URLSearchParams({ projectId });
    if (params?.sessionId)  qs.set('sessionId', params.sessionId);
    if (params?.status)     qs.set('status', params.status);
    if (params?.subcycleId) qs.set('subcycleId', params.subcycleId);
    return request<unknown[]>(`/uat/defects?${qs}`, { extraHeaders: h });
  },
  getDefect:      (id: string, h: H) =>
    request<unknown>(`/uat/defects/${id}`, { extraHeaders: h }),
  createDefect:   (body: unknown, h: H) =>
    request<unknown>('/uat/defects', { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  updateDefect:   (id: string, body: unknown, h: H) =>
    request<unknown>(`/uat/defects/${id}`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
  // Comments
  listComments:   (params: { sessionId?: string; defectId?: string }, h: H) => {
    const qs = new URLSearchParams();
    if (params.sessionId) qs.set('sessionId', params.sessionId);
    if (params.defectId)  qs.set('defectId',  params.defectId);
    return request<unknown[]>(`/uat/comments?${qs}`, { extraHeaders: h });
  },
  createComment:  (body: unknown, h: H) =>
    request<unknown>('/uat/comments', { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  updateComment:  (id: string, content: string, h: H) =>
    request<unknown>(`/uat/comments/${id}`, { method: 'PATCH', body: JSON.stringify({ content }), extraHeaders: h }),
  deleteComment:  (id: string, h: H) =>
    request<void>(`/uat/comments/${id}`, { method: 'DELETE', extraHeaders: h }),
};

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------
export const documentsApi = {
  list:           (projectId: string, h: H) =>
    request<unknown[]>(`/documents?projectId=${encodeURIComponent(projectId)}`, { extraHeaders: h }),
  get:            (id: string, h: H) =>
    request<unknown>(`/documents/${id}`, { extraHeaders: h }),
  versions:       (id: string, h: H) =>
    request<unknown[]>(`/documents/${id}/versions`, { extraHeaders: h }),
  create:         (body: unknown, h: H) =>
    request<unknown>('/documents', { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  generate:       (body: unknown, h: H) =>
    request<unknown>('/documents/generate', { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  update:         (id: string, body: unknown, h: H) =>
    request<unknown>(`/documents/${id}`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
  delete:         (id: string, h: H) =>
    request<void>(`/documents/${id}`, { method: 'DELETE', extraHeaders: h }),
  // Sections
  getSections:    (docId: string, h: H) =>
    request<unknown[]>(`/documents/${docId}/sections`, { extraHeaders: h }),
  getSection:     (docId: string, key: string, h: H) =>
    request<unknown>(`/documents/${docId}/sections/${key}`, { extraHeaders: h }),
  upsertSection:  (docId: string, body: unknown, h: H) =>
    request<unknown>(`/documents/${docId}/sections`, { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  updateSection:  (docId: string, key: string, body: unknown, h: H) =>
    request<unknown>(`/documents/${docId}/sections/${key}`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
  regenSection:   (docId: string, key: string, body: unknown, h: H) =>
    request<unknown>(`/documents/${docId}/sections/${key}/regenerate`, { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  // Export
  exportDocx:     (id: string, h: H) =>
    requestBlob(`/documents/${id}/export/docx`, h),
};

// ---------------------------------------------------------------------------
// Approvals
// ---------------------------------------------------------------------------
export const approvalsApi = {
  list:    (projectId: string, h: H) =>
    request<unknown[]>(`/approvals?projectId=${encodeURIComponent(projectId)}`, { extraHeaders: h }),
  get:     (id: string, h: H) =>
    request<unknown>(`/approvals/${id}`, { extraHeaders: h }),
  create:  (body: unknown, h: H) =>
    request<unknown>('/approvals', { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  decide:  (approvalId: string, stepId: string, body: unknown, h: H) =>
    request<unknown>(`/approvals/${approvalId}/steps/${stepId}/decide`, { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  cancel:  (id: string, reason: string, h: H) =>
    request<unknown>(`/approvals/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }), extraHeaders: h }),
  getAudit: (id: string, h: H) =>
    request<unknown[]>(`/approvals/${id}/audit`, { extraHeaders: h }),
  inbox:   (h: H) =>
    request<unknown[]>('/approvals/inbox', { extraHeaders: h }),
  forEntity: (entityType: string, entityId: string, h: H) =>
    request<unknown>(`/approvals/entity/${entityType}/${entityId}`, { extraHeaders: h }),
};

// ---------------------------------------------------------------------------
// Approval Templates
// ---------------------------------------------------------------------------
export const approvalTemplatesApi = {
  list:   (projectId: string, h: H) =>
    request<unknown[]>(`/approval-templates?projectId=${encodeURIComponent(projectId)}`, { extraHeaders: h }),
  create: (body: unknown, h: H) =>
    request<unknown>('/approval-templates', { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  update: (id: string, body: unknown, h: H) =>
    request<unknown>(`/approval-templates/${id}`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
  delete: (id: string, h: H) =>
    request<void>(`/approval-templates/${id}`, { method: 'DELETE', extraHeaders: h }),
};

// ---------------------------------------------------------------------------
// Custom Roles
// ---------------------------------------------------------------------------
export const rolesApi = {
  list:           (workspaceId: string, h: H) =>
    request<unknown[]>(`/roles?workspaceId=${encodeURIComponent(workspaceId)}`, { extraHeaders: h }),
  get:            (id: string, h: H) =>
    request<unknown>(`/roles/${id}`, { extraHeaders: h }),
  create:         (body: unknown, h: H) =>
    request<unknown>('/roles', { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  update:         (id: string, body: unknown, h: H) =>
    request<unknown>(`/roles/${id}`, { method: 'PATCH', body: JSON.stringify(body), extraHeaders: h }),
  updatePerms:    (id: string, permissions: Record<string, boolean>, h: H) =>
    request<unknown>(`/roles/${id}/permissions`, { method: 'PATCH', body: JSON.stringify({ permissions }), extraHeaders: h }),
  delete:         (id: string, h: H) =>
    request<void>(`/roles/${id}`, { method: 'DELETE', extraHeaders: h }),
  permissionKeys: (h: H) =>
    request<string[]>('/roles/permission-keys', { extraHeaders: h }),
};

// ---------------------------------------------------------------------------
// Audio / Upload
// ---------------------------------------------------------------------------
// POST /audio/upload's response — returned as soon as the file is stored;
// transcription/requirements-extraction/Discovery-matching happen in the
// background afterward. See audioApi.getTranscription for the poll shape.
export interface AudioUploadKickoff {
  uploadId: string;
  transcriptionId: string;
  status: 'pending';
}

// GET /audio/:id/transcription — the job-status poll target.
export interface TranscriptionStatus {
  id: string;
  audio_upload_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  transcript_text: string | null;
  language: string | null;
  confidence: number | null;
  whisper_model: string | null;
  error_message: string | null;
  chunks_total: number | null;
  chunks_completed: number;
  requirements_extracted: number | null;
  discovery_suggestions_created: number | null;
  gaps_created: number | null;
}

// GET /audio/:id — audio_uploads columns (au.*) + joined transcription
// summary + a computed `stage` collapsing both into the single
// progression ProcessingStatus.tsx displays.
export interface AudioUploadDetail {
  id: string;
  project_id: string;
  filename: string;
  storage_url: string | null;
  file_size_bytes: number | null;
  duration_secs: number | null;
  mime_type: string | null;
  uploaded_by: string | null;
  uploaded_at: string;
  transcribed_at: string | null;
  transcription_id: string | null;
  transcription_status: 'pending' | 'processing' | 'completed' | 'failed' | null;
  transcript_text: string | null;
  language: string | null;
  confidence: number | null;
  whisper_model: string | null;
  error_message: string | null;
  chunks_total: number | null;
  chunks_completed: number | null;
  requirements_extracted: number | null;
  discovery_suggestions_created: number | null;
  gaps_created: number | null;
  stage: 'uploaded' | 'transcribing' | 'extracting' | 'matching' | 'requirements_error' | 'done' | 'error';
}

export const audioApi = {
  list:    (projectId: string, h: H) =>
    request<unknown[]>(`/audio?projectId=${encodeURIComponent(projectId)}`, { extraHeaders: h }),
  get:     (id: string, h: H) =>
    request<AudioUploadDetail>(`/audio/${id}`, { extraHeaders: h }),
  // The project's most recent upload with its computed `stage` (no transcript
  // text), or null when it has none — what the Discovery page uses to resume
  // after a refresh. See lib/latestUpload.ts.
  latest:  (projectId: string, h: H) =>
    request<Omit<AudioUploadDetail, 'transcript_text'> | null>(`/audio/latest?projectId=${encodeURIComponent(projectId)}`, { extraHeaders: h }),
  upload:  (formData: FormData, h: H) =>
    requestFormData<AudioUploadKickoff>('/audio/upload', formData, h),
  getTranscription: (uploadId: string, h: H) =>
    request<TranscriptionStatus>(`/audio/${uploadId}/transcription`, { extraHeaders: h }),
};

// ---------------------------------------------------------------------------
// Sign-offs
// ---------------------------------------------------------------------------
export const signoffApi = {
  list:   (projectId: string, h: H) =>
    request<unknown[]>(`/signoffs?projectId=${encodeURIComponent(projectId)}`, { extraHeaders: h }),
  get:    (id: string, h: H) =>
    request<unknown>(`/signoffs/${id}`, { extraHeaders: h }),
  create: (body: unknown, h: H) =>
    request<unknown>('/signoffs', { method: 'POST', body: JSON.stringify(body), extraHeaders: h }),
  forEntity: (entityType: string, entityId: string, h: H) =>
    request<unknown[]>(`/signoffs/entity/${entityType}/${entityId}`, { extraHeaders: h }),
};
